import { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Send, X } from "lucide-react";
import { useOverlayLock } from "@/contexts/UiOverlayContext";
import {
  useAssistantHistory,
  useClearAssistantHistory,
  useSendAssistantMessage,
} from "@/lib/assistant";
import RoboBallImage from "@/components/RoboBallImage";

const SUGGESTED_PROMPTS = [
  "Who's favorite to win the 2026 World Cup?",
  "How do I earn FiguTokens?",
  "Tell me the story of Messi's career",
  "How does FiguMatch trading work?",
];

export default function AssistantPanel({ open, onOpenChange }) {
  // The panel is itself an overlay (so the FAB stays hidden under it)
  useOverlayLock(open);

  const { data, isLoading } = useAssistantHistory(open);
  const send = useSendAssistantMessage();
  const clear = useClearAssistantHistory();

  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const scrollRef = useRef(null);

  const messages = data?.messages || [];

  // Auto-scroll to bottom when messages length or pending state changes
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, send.isPending]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  async function submit(content) {
    const value = (content ?? text).trim();
    if (!value || send.isPending) return;
    setErr("");
    setText("");
    try {
      await send.mutateAsync(value);
    } catch (e) {
      if (e.code === "assistant_unavailable") {
        setErr("Robo-Ball is taking a break. Try again in a moment.");
      } else {
        setErr("Couldn't send your message. Please try again.");
      }
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    submit();
  }

  async function handleClear() {
    setConfirmClear(false);
    await clear.mutateAsync();
    setErr("");
  }

  return (
    <>
      {/* Backdrop on mobile only (desktop is a non-modal card) */}
      <button
        type="button"
        aria-label="Close assistant"
        onClick={() => onOpenChange(false)}
        data-testid="assistant-backdrop"
        className="md:hidden fixed inset-0 bg-black/50"
        style={{ zIndex: "var(--z-modal)" }}
      />

      <section
        role="dialog"
        aria-label="Robo-Ball assistant"
        data-testid="assistant-panel"
        data-panel-id="assistant-chat-panel"
        className={
          "fixed bg-[#0A0A0A] border border-white/10 text-white flex flex-col " +
          // Mobile: bottom sheet 90vh ; Desktop: 400×600 anchored bottom-right
          "left-0 right-0 bottom-0 h-[90vh] rounded-t-2xl " +
          "md:left-auto md:right-6 md:bottom-6 md:top-auto md:h-[600px] md:w-[400px] md:rounded-xl " +
          "shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.8)] md:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]"
        }
        style={{ zIndex: "var(--z-modal)" }}
      >
        {/* Mobile grabber */}
        <div className="md:hidden flex justify-center pt-2">
          <span className="h-1 w-10 bg-white/20 rounded-full" />
        </div>

        {/* Header */}
        <header className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-white/10">
          <FabAvatar />
          <h2 className="font-display text-base font-semibold text-white flex-1">
            Robo-Ball
          </h2>
          <button
            type="button"
            onClick={() => setConfirmClear(true)}
            data-testid="assistant-restart-button"
            disabled={messages.length === 0 || clear.isPending}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-white/10 text-[#A1A1AA] hover:text-white hover:bg-white/5 text-xs font-body disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Restart conversation"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Restart
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="assistant-close-button"
            aria-label="Close assistant"
            className="h-8 w-8 rounded-md flex items-center justify-center text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Message list */}
        <div
          ref={scrollRef}
          data-testid="assistant-message-list"
          className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-5 py-5 space-y-3"
        >
          {isLoading ? (
            <p className="text-[#A1A1AA] text-sm font-body text-center">
              Loading…
            </p>
          ) : messages.length === 0 ? (
            <EmptyState onPickPrompt={(p) => submit(p)} pending={send.isPending} />
          ) : (
            messages.map((m, i) => (
              <MessageBubble key={`${m.created_at}-${i}`} m={m} />
            ))
          )}
          {send.isPending ? <TypingDots /> : null}
        </div>

        {/* Input bar */}
        <form
          onSubmit={handleSubmit}
          data-testid="assistant-input-form"
          className="border-t border-white/10"
          style={{
            paddingBottom: "max(12px, env(safe-area-inset-bottom))",
          }}
        >
          {err ? (
            <p
              data-testid="assistant-input-error"
              className="text-xs text-[#FF6B6B] font-body px-4 sm:px-5 pt-2"
            >
              {err}
            </p>
          ) : null}
          <div className="flex items-end gap-2 px-4 sm:px-5 py-3">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              data-testid="assistant-input"
              rows={1}
              maxLength={2000}
              placeholder="Ask Robo-Ball anything…"
              className="flex-1 min-h-[40px] max-h-[120px] resize-none bg-[#121212] border border-white/10 rounded-md px-3 py-2 text-white placeholder:text-white/30 font-body text-sm focus:outline-none focus:ring-1 focus:ring-[#E5B83A] focus:border-[#E5B83A]"
            />
            <button
              type="submit"
              disabled={!text.trim() || send.isPending}
              data-testid="assistant-send-button"
              className="h-10 w-10 rounded-md bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black flex items-center justify-center hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity shrink-0"
              aria-label="Send message"
            >
              {send.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
        </form>
      </section>

      {/* Restart confirm dialog (own overlay above the panel) */}
      {confirmClear ? (
        <ConfirmRestart
          onCancel={() => setConfirmClear(false)}
          onConfirm={handleClear}
          pending={clear.isPending}
        />
      ) : null}
    </>
  );
}

function FabAvatar() {
  return (
    <RoboBallImage
      className="h-8 w-8 rounded-full shrink-0 object-cover"
      fallback={
        <span className="h-8 w-8 rounded-full bg-gradient-to-br from-[#E5B83A] to-[#C9971C] shrink-0" />
      }
    />
  );
}

function EmptyState({ onPickPrompt, pending }) {
  return (
    <div data-testid="assistant-empty" className="pt-2 space-y-5">
      <div className="flex flex-col items-center text-center gap-3 mt-3">
        <FabAvatarLarge />
        <p className="font-body text-sm text-white max-w-xs">
          Hi! I&apos;m Robo-Ball ⚽ — ask me anything about FiguMatch.
        </p>
      </div>
      <div className="grid gap-2 pt-1">
        {SUGGESTED_PROMPTS.map((p, i) => (
          <button
            key={p}
            type="button"
            onClick={() => onPickPrompt(p)}
            disabled={pending}
            data-testid={`assistant-prompt-chip-${i}`}
            className="text-left text-sm font-body text-white bg-white/[0.03] hover:bg-white/[0.07] disabled:opacity-40 border border-white/10 hover:border-[#E5B83A]/30 rounded-lg px-3 py-2.5 transition-colors"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function FabAvatarLarge() {
  return (
    <RoboBallImage
      className="h-14 w-14 rounded-full object-cover shadow-[0_8px_24px_-8px_rgba(229,184,58,0.6)]"
      fallback={
        <span className="h-14 w-14 rounded-full bg-gradient-to-br from-[#E5B83A] to-[#C9971C] shadow-[0_8px_24px_-8px_rgba(229,184,58,0.6)]" />
      }
    />
  );
}

function MessageBubble({ m }) {
  const mine = m.role === "user";
  return (
    <div
      data-testid={mine ? "assistant-msg-user" : "assistant-msg-bot"}
      className={"flex items-end gap-2 " + (mine ? "justify-end" : "justify-start")}
    >
      {!mine ? <BotAvatar /> : null}
      <div
        className={
          "max-w-[78%] rounded-2xl px-3.5 py-2 font-body text-sm whitespace-pre-wrap break-words " +
          (mine
            ? "bg-[#E5B83A] text-black rounded-br-md"
            : "bg-[#1a1a1a] text-white rounded-bl-md border border-white/5")
        }
      >
        {m.content}
      </div>
    </div>
  );
}

function BotAvatar() {
  return (
    <RoboBallImage
      className="h-6 w-6 rounded-full object-cover shrink-0 self-end mb-1"
      fallback={
        <span className="h-6 w-6 rounded-full bg-gradient-to-br from-[#E5B83A] to-[#C9971C] shrink-0 self-end mb-1" />
      }
    />
  );
}

function TypingDots() {
  return (
    <div
      data-testid="assistant-typing"
      className="flex items-end gap-2 justify-start"
    >
      <BotAvatar />
      <div className="bg-[#1a1a1a] border border-white/5 rounded-2xl rounded-bl-md px-3.5 py-2.5 flex items-center gap-1">
        <span className="figumatch-typing-dot" />
        <span className="figumatch-typing-dot" style={{ animationDelay: "0.15s" }} />
        <span className="figumatch-typing-dot" style={{ animationDelay: "0.3s" }} />
      </div>
    </div>
  );
}

function ConfirmRestart({ onCancel, onConfirm, pending }) {
  useOverlayLock(true);
  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-4"
      style={{ zIndex: "calc(var(--z-modal) + 1)" }}
    >
      <button
        type="button"
        aria-label="Cancel"
        onClick={onCancel}
        className="absolute inset-0 bg-black/70"
      />
      <div
        data-testid="assistant-confirm-restart"
        className="relative bg-[#0A0A0A] border border-white/10 rounded-xl p-5 max-w-sm w-full"
      >
        <h3 className="font-display text-lg font-bold text-white">
          Restart conversation?
        </h3>
        <p className="font-body text-sm text-[#A1A1AA] mt-2">
          This clears everything Robo-Ball has discussed with you.
        </p>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            data-testid="assistant-confirm-cancel"
            className="h-9 px-3 rounded-md border border-white/15 text-white font-body text-sm hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            data-testid="assistant-confirm-restart-confirm"
            className="h-9 px-3 rounded-md bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black font-semibold text-sm hover:opacity-95 disabled:opacity-40 transition-opacity inline-flex items-center gap-2"
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Restart
          </button>
        </div>
      </div>
    </div>
  );
}
